import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogOut } from "lucide-react";
import { cn } from "@/lib/cn";
import { NAV_ITEMS, NAV_GROUP_LABELS, type NavGroup } from "@/lib/constants";
import { can, isAgencyAdmin, MOCK_USER } from "@/lib/permissions";
import { Avatar } from "@/ui";
import { useCurrentUser, useLogout } from "@/features/auth/auth.hooks";
import { logoutToLogin } from "./sidebar.logic";

const GROUP_ORDER: NavGroup[] = ["operations", "finance", "team"];

export function Sidebar() {
  const { data: currentUser } = useCurrentUser();
  const user = currentUser ?? MOCK_USER;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logout = useLogout();

  return (
    <aside className="w-[var(--sidebar-w)] min-w-[var(--sidebar-w)] bg-bg2 border-r border-border flex flex-col overflow-y-auto scroll-thin z-[100]">
      <div className="flex items-center gap-2.5 px-4 h-[var(--topbar-h)] border-b border-border shrink-0">
        <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-primary text-white text-[13px] font-bold shadow-sm">
          F
        </div>
        <span className="font-display text-[15px] font-bold text-text">Fad Agency</span>
      </div>

      <nav className="flex-1 px-2.5 py-3">
        {GROUP_ORDER.map((group) => {
          const items = NAV_ITEMS.filter(
            (i) =>
              i.group === group &&
              (!i.permission || can(user, i.permission)) &&
              (!i.adminOnly || isAgencyAdmin(user)) &&
              (!i.restrictRoles || i.restrictRoles.some((r) => user.roles.includes(r as never))),
          );
          if (items.length === 0) return null;
          return (
            <div key={group}>
              <div className="px-2 pt-3 pb-1.5 text-[9px] font-bold uppercase tracking-[0.12em] text-text3">
                {t(NAV_GROUP_LABELS[group].key)}
              </div>
              {items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-2.5 px-2.5 py-2 rounded-sm text-[12.5px] font-semibold mb-1 transition-all",
                        isActive
                          ? "bg-primary-dim text-primary border border-primary/20"
                          : "text-text3 border border-transparent hover:bg-white/[0.04] hover:text-text2",
                      )
                    }
                  >
                    <Icon className="h-[15px] w-[15px] shrink-0" />
                    <span className="flex-1">{t(item.labelKey)}</span>
                    {item.badge && (
                      <span
                        className={cn(
                          "text-[10px] font-semibold px-1.5 rounded-full",
                          item.badge.tone === "danger"
                            ? "bg-danger-dim text-danger"
                            : "bg-primary-dim text-primary",
                        )}
                      >
                        {item.badge.value}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div className="p-2.5 border-t border-border shrink-0">
        <div className="flex items-center gap-2.5 px-2.5 py-2.5 rounded-sm">
          <Avatar name={user.name} color="violet" />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-text truncate">{user.name}</div>
            <div className="text-[10px] text-text3">{user.roles.map((r) => t(`common.roles.${r}`)).join(", ")}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => logoutToLogin(logout, navigate)}
          className="mt-1 flex w-full items-center gap-2.5 rounded-sm border border-transparent px-2.5 py-2 text-[12px] font-semibold text-text3 transition-colors hover:border-danger/25 hover:bg-danger-dim hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger/35"
          aria-label={t("common.logout")}
          title={t("common.logout")}
        >
          <LogOut className="h-[15px] w-[15px] shrink-0" />
          <span className="truncate">{t("common.logout")}</span>
        </button>
      </div>
    </aside>
  );
}
